import React, { useCallback, useMemo } from 'react';
import { LuWorkflow } from 'react-icons/lu';

import { GrowthAllocationType, Strategy, StrategyType } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { CollapsibleSection } from '@/components/CollapsibleSection';
import { TickerLogo } from '@/components/TickerLogo';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useAssetsByIds } from '@/hooks/market-data-queries';
import { formatPercent } from 'utils';

import { extractSecurityIdentifiers, getStrategyTypeLabel } from './strategy-utils';
import { getStrategyDescription } from './strategyDescriptions';

interface StrategyDetailsDialogProps {
    strategy?: Strategy;
    onClose: () => void;
    open: boolean;
}

export function StrategyDetailsDialog({ strategy, onClose, open }: StrategyDetailsDialogProps) {
    const securityIdentifiers = useMemo(() => {
        if (!strategy) return [];
        return extractSecurityIdentifiers(strategy.assetAllocations);
    }, [strategy]);

    const { data: assets = [] } = useAssetsByIds(securityIdentifiers);

    const getAssetInfo = useCallback(
        (securityId: string): AssetInfo | undefined => {
            return assets.find((a) => a.sId === securityId);
        },
        [assets],
    );

    if (!strategy) return null;

    return (
        <Dialog open={open} onOpenChange={(newOpen) => !newOpen && onClose()}>
            <DialogContent className="flex h-[85vh] max-w-4xl flex-col bg-white tracking-[-0.02em]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <LuWorkflow className="h-5 w-5 text-gray-600" />
                        Strategy Details
                    </DialogTitle>
                    <DialogDescription></DialogDescription>
                </DialogHeader>
                <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 flex-1 overflow-y-auto px-1">
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <div className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
                                Strategy Name
                            </div>
                            <div className="text-cocoa-1000 font-medium">{strategy.strategyName}</div>
                        </div>

                        <div className="space-y-2">
                            <div className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
                                Strategy Type
                            </div>
                            <div className="text-cocoa-1000 font-medium">{getStrategyTypeLabel(strategy)}</div>
                        </div>

                        {strategy.strategyType === StrategyType.CORE && strategy.growthAllocationType && (
                            <>
                                {getStrategyDescription(strategy.growthAllocationType) && (
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
                                            Description
                                        </div>
                                        {getStrategyDescription(strategy.growthAllocationType)}
                                    </div>
                                )}
                            </>
                        )}

                        {strategy.strategyType !== StrategyType.CORE && (
                            <div className="space-y-3">
                                <div className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
                                    Assets
                                </div>

                                <div className="space-y-2">
                                    {[...strategy.assetAllocations]
                                        .sort((a, b) => parseFloat(b.weight) - parseFloat(a.weight))
                                        .map((asset) => {
                                            const assetInfo = getAssetInfo(asset.securityId);
                                            const hasReplacements =
                                                asset.replacementAssets && asset.replacementAssets.length > 0;

                                            return (
                                                <CollapsibleSection
                                                    key={asset.securityId}
                                                    title={
                                                        <div className="mr-4 flex w-full items-center justify-between gap-4">
                                                            <div className="flex items-center gap-3">
                                                                <TickerLogo
                                                                    tickerSymbol={assetInfo?.symbol || asset.securityId}
                                                                    companyName={assetInfo?.description}
                                                                    className="h-8 min-h-8 w-8 min-w-8 flex-shrink-0 rounded object-cover"
                                                                    fallbackIconClassName="w-8 h-8"
                                                                />
                                                                <div className="flex flex-col items-start gap-0.5 text-left">
                                                                    <span className="font-medium">
                                                                        {assetInfo?.symbol || asset.securityId} -{' '}
                                                                        {assetInfo?.description || 'Unknown'}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500">
                                                                        {asset.replacementAssets?.length || 0}{' '}
                                                                        replacement
                                                                        {asset.replacementAssets?.length !== 1
                                                                            ? 's'
                                                                            : ''}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-medium text-gray-600">
                                                                    Target
                                                                </span>
                                                                <span className="w-16 text-right text-sm font-semibold">
                                                                    {formatPercent(parseFloat(asset.weight) * 100)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    }
                                                    defaultOpen={false}
                                                >
                                                    <div className="space-y-2">
                                                        <div className="text-sm font-medium text-gray-700">
                                                            Replacements
                                                        </div>
                                                        {!hasReplacements ? (
                                                            <div className="text-sm text-gray-500">No replacements</div>
                                                        ) : (
                                                            <div className="space-y-2">
                                                                {asset.replacementAssets.map((replacement, index) => {
                                                                    const replacementAsset = getAssetInfo(
                                                                        replacement.securityId,
                                                                    );
                                                                    return (
                                                                        <div
                                                                            key={replacement.securityId}
                                                                            className="flex items-center gap-2"
                                                                        >
                                                                            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-700">
                                                                                {index + 1}
                                                                            </span>
                                                                            <div className="border-border flex flex-1 items-center gap-2 rounded border bg-white p-2">
                                                                                <TickerLogo
                                                                                    tickerSymbol={
                                                                                        replacementAsset?.symbol ||
                                                                                        replacement.securityId
                                                                                    }
                                                                                    companyName={
                                                                                        replacementAsset?.description
                                                                                    }
                                                                                    className="h-6 min-h-6 w-6 min-w-6 flex-shrink-0 rounded object-cover"
                                                                                    fallbackIconClassName="w-6 h-6"
                                                                                />
                                                                                <span className="text-sm">
                                                                                    {replacementAsset?.symbol ||
                                                                                        replacement.securityId}{' '}
                                                                                    -{' '}
                                                                                    {replacementAsset?.description ||
                                                                                        'Unknown'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </CollapsibleSection>
                                            );
                                        })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
